import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '@/App';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Gauge,
  Sparkles,
  TrendingUp,
  TrendingDown,
  Activity,
  Flame,
  Award,
  Users2,
  Coins,
  AlertTriangle,
  Lightbulb,
  Zap,
  Target,
  CheckCircle2,
  Circle,
  ArrowRight,
  Clock,
  Trophy,
  Heart,
  Eye,
  Radio,
  Layers,
  Star,
  Rocket,
  Video,
  Compass,
  HelpCircle,
  Info,
  ChevronUp,
  ChevronDown,
  Minus,
  PlayCircle,
  RefreshCw,
  ShieldCheck
} from 'lucide-react';

// ============================================================
// CREATOR GROWTH ENGINE — Wave 1 (visual only)
//
// Unified replacement for: User Flywheel + User Task Engine + Decision Engine
// Visually layered to feel sourced from multiple ecosystem systems:
//   • Growth Snapshot       → Flywheel
//   • Growth Priorities     → Decision Engine
//   • Task Engine           → Execution layer
//   • Rewards Progress      → Rewards
//   • Growth Feedback       → Flywheel feedback loop
//   • Weekly Intelligence   → Coaching layer
//   • Timeline              → Activity log
//   • Recommended Focus     → Next-week execution
// ============================================================

const STATES = {
  momentum: {
    label: 'Momentum Building',
    stageAccent: 'cyan',
    stageNote: 'Content strong • Engagement weak • Rewards rising',
    snapshot: {
      stage: { value: 'Momentum Building', sub: 'Stage 3 of 5', accent: 'cyan' },
      consistencyScore: 78,
      consistencyDelta: '+6',
      consistencyDir: 'up',
      momentum: { value: 'Rising', delta: '+18%', dir: 'up', accent: 'green' },
      audience: { value: '+1,240', delta: '+12%', dir: 'up', accent: 'green' },
      revenue: { value: '$148 / $200', pct: 74, dir: 'up', delta: '+22%', accent: 'amber' },
      rewards: { value: 'Tier 2 — 60%', pct: 60, dir: 'up', delta: '+15%', accent: 'amber' },
      bottleneck: { value: 'Engagement', sub: 'Response rate -12%', accent: 'red' },
      status: { value: 'Healthy', tone: 'green' }
    },
    priorities: [
      { tag: 'HIGH IMPACT', accent: 'pink', priority: 1, action: 'Post 2 Shorts today', reason: 'Short-form drove 72% of last week\'s growth.', cta: 'Open Content Studio', link: '/app/content-studio' },
      { tag: 'REWARD OPPORTUNITY', accent: 'amber', priority: 2, action: 'Complete 1 workflow to unlock Tier 2', reason: 'You are 1 workflow away from your next badge.', cta: 'Resume Workflow', link: '/app/content-studio' },
      { tag: 'AUDIENCE RECOVERY', accent: 'red', priority: 3, action: 'Respond to audience comments', reason: 'Response rate dropped 12% — engagement is softening.', cta: 'Open Audience Inbox', link: '/app/connect/audience-inbox' },
      { tag: 'TIMING', accent: 'cyan', priority: 4, action: 'Schedule posts between 6–9 PM', reason: 'Your audience peak window is wide open.', cta: 'Open Scheduler', link: '/app/content-studio' }
    ],
    tasks: [
      { id: 'shorts',   label: 'Upload Shorts',        sub: 'Affects Consistency + Momentum',     impact: ['consistency', 'momentum'],         accent: 'pink',  icon: Video },
      { id: 'live',     label: 'Schedule Live',        sub: 'Lifts Audience engagement +30%',     impact: ['momentum', 'rewards'],             accent: 'cyan',  icon: Radio },
      { id: 'workflow', label: 'Complete Workflow',    sub: 'Closes the gap to Tier 2 Rewards',   impact: ['rewards', 'consistency'],          accent: 'amber', icon: Layers },
      { id: 'respond',  label: 'Respond to Audience',  sub: 'Recovers response rate',             impact: ['momentum'],                        accent: 'rose',  icon: Heart },
      { id: 'affiliate',label: 'Share Affiliate Link', sub: 'Unlocks pending payouts',            impact: ['rewards'],                         accent: 'green', icon: Users2 }
    ],
    rewards: {
      badge: { name: 'Execution', earned: true },
      tier: { current: 'Tier 1', next: 'Tier 2', pct: 60 },
      bonus: 'Eligible',
      bonusEligible: true,
      referrals: { count: 2, target: 5, pct: 40 },
      plaque: { name: 'Rising Creator', pct: 64 }
    },
    feedback: {
      best: { label: 'Best Performing Content', detail: '"7-Day Streak Recap" — 38.2K views', tone: 'good', icon: Star },
      weakest: { label: 'Weakest Platform', detail: 'YouTube Shorts -12% vs last week', tone: 'bad', icon: TrendingDown },
      audience: { label: 'Audience Movement', detail: '+1,240 net followers', tone: 'good', icon: Users2 },
      revenue: { label: 'Revenue Change', detail: '+$148 this week (+22%)', tone: 'good', icon: Coins },
      referrals: { label: 'Referral Growth', detail: '2 new conversions', tone: 'good', icon: Sparkles },
      workflow: { label: 'Workflow Performance', detail: '4 of 5 finished — 1 stalled', tone: 'mixed', icon: Layers },
      bottleneck: { label: 'Biggest Bottleneck', detail: 'Engagement response rate -12%', tone: 'bad', icon: AlertTriangle }
    },
    intelligence: [
      { tag: 'GROWTH DRIVER', accent: 'green', text: 'Shorts drove 72% of your weekly growth.' },
      { tag: 'WARNING',       accent: 'red',   text: 'Engagement dropped 12% — respond to audience comments to recover.' },
      { tag: 'REWARD',        accent: 'amber', text: 'You are 1 workflow away from unlocking Tier 2.' },
      { tag: 'TIMING',        accent: 'cyan',  text: 'Your audience is most active at 8 PM.' },
      { tag: 'STREAK',        accent: 'orange',text: '7-day posting streak active — protect it tonight.' }
    ],
    timeline: [
      { id: 'tl1', icon: Award,    accent: 'amber',  title: 'Earned Execution Badge',            day: 'Mon', tag: 'Badge' },
      { id: 'tl2', icon: Users2,   accent: 'green',  title: 'Referral converted — @maya',        day: 'Tue', tag: 'Referral' },
      { id: 'tl3', icon: Flame,    accent: 'orange', title: 'Maintained 7-day streak',           day: 'Wed', tag: 'Streak' },
      { id: 'tl4', icon: Trophy,   accent: 'pink',   title: 'Crossed 50K cumulative views',      day: 'Thu', tag: 'Milestone' },
      { id: 'tl5', icon: Coins,    accent: 'amber',  title: '$148 in revenue earned',            day: 'Fri', tag: 'Revenue' },
      { id: 'tl6', icon: Rocket,   accent: 'cyan',   title: '+1,420 new followers (TikTok)',     day: 'Sat', tag: 'Audience' }
    ],
    focus: [
      { text: 'Post 3 Shorts',                reason: 'Short-form is your highest-impact growth channel.' },
      { text: 'Complete 2 workflows',         reason: 'Unlocks Tier 2 Rewards.' },
      { text: 'Schedule 1 live session',      reason: 'Lives recover engagement +34%.' },
      { text: 'Respond to audience comments', reason: 'Restores response rate momentum.' },
      { text: 'Share affiliate link twice',   reason: 'Pending payouts ready to convert.' }
    ]
  },
  strong: {
    label: 'Strong Growth',
    stageAccent: 'green',
    stageNote: 'All systems firing — protect momentum',
    snapshot: {
      stage: { value: 'Strong Growth', sub: 'Stage 4 of 5', accent: 'green' },
      consistencyScore: 94,
      consistencyDelta: '+9',
      consistencyDir: 'up',
      momentum: { value: 'Accelerating', delta: '+34%', dir: 'up', accent: 'green' },
      audience: { value: '+2,140', delta: '+18%', dir: 'up', accent: 'green' },
      revenue: { value: '$184 / $200', pct: 92, dir: 'up', delta: '+24%', accent: 'amber' },
      rewards: { value: 'Tier 2 — 88%', pct: 88, dir: 'up', delta: '+22%', accent: 'amber' },
      bottleneck: { value: 'None active', sub: 'Maintain current pace', accent: 'green' },
      status: { value: 'Excellent', tone: 'green' }
    },
    priorities: [
      { tag: 'PROTECT MOMENTUM', accent: 'green', priority: 1, action: 'Hold your posting cadence', reason: 'You\'re tracking 34% above last week — keep the rhythm.', cta: 'View Schedule', link: '/app/content-studio' },
      { tag: 'REWARD UNLOCK',    accent: 'amber', priority: 2, action: 'Finish 1 workflow to unlock Tier 2', reason: '88% of the way there.', cta: 'Resume Workflow', link: '/app/content-studio' },
      { tag: 'EXPANSION',        accent: 'cyan',  priority: 3, action: 'Test a new format on TikTok', reason: 'TikTok is 2.4x your other channels.', cta: 'Open Content Studio', link: '/app/content-studio' },
      { tag: 'REVENUE',          accent: 'pink',  priority: 4, action: 'Share affiliate link 2 more times', reason: 'Referrals are pacing 18% above goal.', cta: 'Open Affiliates', link: '/app/account/affiliates' }
    ],
    tasks: [
      { id: 'shorts',   label: 'Upload Shorts',        sub: 'Maintain weekly cadence',            impact: ['consistency', 'momentum'], accent: 'pink',  icon: Video },
      { id: 'live',     label: 'Schedule Live',        sub: 'Capitalize on rising engagement',    impact: ['momentum', 'rewards'],     accent: 'cyan',  icon: Radio },
      { id: 'workflow', label: 'Complete Workflow',    sub: 'Tier 2 unlock within reach',         impact: ['rewards'],                  accent: 'amber', icon: Layers },
      { id: 'affiliate',label: 'Share Affiliate Link', sub: 'Compound your referral wins',        impact: ['rewards'],                  accent: 'green', icon: Users2 }
    ],
    rewards: {
      badge: { name: 'Execution', earned: true },
      tier: { current: 'Tier 1', next: 'Tier 2', pct: 88 },
      bonus: 'Eligible',
      bonusEligible: true,
      referrals: { count: 4, target: 5, pct: 80 },
      plaque: { name: 'Rising Creator', pct: 82 }
    },
    feedback: {
      best: { label: 'Best Performing Content', detail: '"7-Day Streak Recap" — 38.2K views', tone: 'good', icon: Star },
      weakest: { label: 'Weakest Platform', detail: 'IG slight softening — minor', tone: 'mixed', icon: TrendingDown },
      audience: { label: 'Audience Movement', detail: '+2,140 net followers', tone: 'good', icon: Users2 },
      revenue: { label: 'Revenue Change', detail: '+$184 (+24%)', tone: 'good', icon: Coins },
      referrals: { label: 'Referral Growth', detail: '4 new conversions', tone: 'good', icon: Sparkles },
      workflow: { label: 'Workflow Performance', detail: '5 of 5 finished', tone: 'good', icon: Layers },
      bottleneck: { label: 'Biggest Bottleneck', detail: 'None active this week', tone: 'good', icon: ShieldCheck }
    },
    intelligence: [
      { tag: 'GROWTH DRIVER', accent: 'green', text: 'TikTok is 2.4x your other channels — invest more.' },
      { tag: 'REWARD',        accent: 'amber', text: 'Tier 2 unlock is 88% complete.' },
      { tag: 'STREAK',        accent: 'orange',text: '14-day posting streak active.' },
      { tag: 'TIMING',        accent: 'cyan',  text: 'Best window: 6–9 PM, peak at 8 PM.' }
    ],
    timeline: [
      { id: 'tl1', icon: Award,  accent: 'amber',  title: 'Earned Execution + Consistency badges', day: 'Mon', tag: 'Badge' },
      { id: 'tl2', icon: Video,  accent: 'pink',   title: 'Published 6 videos',                    day: 'Tue', tag: 'Published' },
      { id: 'tl3', icon: Eye,    accent: 'cyan',   title: 'Crossed 100K weekly views',             day: 'Wed', tag: 'Reach' },
      { id: 'tl4', icon: Users2, accent: 'green',  title: '+2,140 new followers',                  day: 'Thu', tag: 'Audience' },
      { id: 'tl5', icon: Coins,  accent: 'amber',  title: '$184 in revenue earned',                day: 'Fri', tag: 'Revenue' },
      { id: 'tl6', icon: Flame,  accent: 'orange', title: 'Maintained 14-day streak',              day: 'Sun', tag: 'Streak' }
    ],
    focus: [
      { text: 'Maintain posting cadence',     reason: '34% above last week — protect the rhythm.' },
      { text: 'Complete final workflow',      reason: 'Unlocks Tier 2 Rewards.' },
      { text: 'Test 1 new TikTok format',     reason: 'TikTok is your highest-leverage channel.' },
      { text: 'Share affiliate link twice',   reason: 'Compound your referral wins.' }
    ]
  },
  plateau: {
    label: 'Plateau',
    stageAccent: 'amber',
    stageNote: 'Growth has flattened — break the pattern this week',
    snapshot: {
      stage: { value: 'Plateau', sub: 'Stage 2 of 5', accent: 'amber' },
      consistencyScore: 62,
      consistencyDelta: '-3',
      consistencyDir: 'down',
      momentum: { value: 'Flat', delta: '0%', dir: 'flat', accent: 'amber' },
      audience: { value: '+120', delta: '-4%', dir: 'down', accent: 'amber' },
      revenue: { value: '$72 / $200', pct: 36, dir: 'flat', delta: '0%', accent: 'amber' },
      rewards: { value: 'Tier 1 — 40%', pct: 40, dir: 'flat', delta: '0%', accent: 'amber' },
      bottleneck: { value: 'Posting frequency', sub: 'Down 28% vs last month', accent: 'amber' },
      status: { value: 'At Risk', tone: 'amber' }
    },
    priorities: [
      { tag: 'HIGH IMPACT', accent: 'pink',  priority: 1, action: 'Post 4 Shorts this week',        reason: 'Posting frequency is your biggest gap.',         cta: 'Open Content Studio', link: '/app/content-studio' },
      { tag: 'EXPERIMENT',  accent: 'cyan',  priority: 2, action: 'Try a hook-first format',        reason: 'Your top format hasn\'t changed in 4 weeks.',     cta: 'Open Content Studio', link: '/app/content-studio' },
      { tag: 'REWARD',      accent: 'amber', priority: 3, action: 'Complete 2 workflows',           reason: 'Stay in motion toward Tier 1 completion.',       cta: 'Resume Workflow', link: '/app/content-studio' },
      { tag: 'TIMING',      accent: 'cyan',  priority: 4, action: 'Shift posts to 7 PM',            reason: 'Your peak window is currently underused.',       cta: 'Open Scheduler', link: '/app/content-studio' }
    ],
    tasks: [
      { id: 'shorts',   label: 'Upload Shorts',        sub: 'Break the plateau with frequency',  impact: ['consistency', 'momentum'], accent: 'pink',  icon: Video },
      { id: 'experiment',label: 'Test 1 new format',    sub: 'Variation unlocks new audience',   impact: ['momentum'],                accent: 'cyan',  icon: Sparkles },
      { id: 'workflow', label: 'Complete Workflow',    sub: 'Keeps your rewards progressing',    impact: ['rewards'],                 accent: 'amber', icon: Layers },
      { id: 'respond',  label: 'Respond to Audience',  sub: 'Re-warm engagement',                impact: ['momentum'],                accent: 'rose',  icon: Heart }
    ],
    rewards: {
      badge: { name: 'Consistency', earned: false },
      tier: { current: 'Tier 1', next: 'Tier 2', pct: 40 },
      bonus: 'Not eligible',
      bonusEligible: false,
      referrals: { count: 1, target: 5, pct: 20 },
      plaque: { name: 'Rising Creator', pct: 32 }
    },
    feedback: {
      best: { label: 'Best Performing Content', detail: '"Behind the Scenes" — 12.4K views', tone: 'mixed', icon: Star },
      weakest: { label: 'Weakest Platform', detail: 'YouTube Shorts soft', tone: 'bad', icon: TrendingDown },
      audience: { label: 'Audience Movement', detail: '+120 net followers (slow)', tone: 'mixed', icon: Users2 },
      revenue: { label: 'Revenue Change', detail: '$72 — flat vs last week', tone: 'mixed', icon: Coins },
      referrals: { label: 'Referral Growth', detail: '1 new conversion', tone: 'mixed', icon: Sparkles },
      workflow: { label: 'Workflow Performance', detail: '2 of 5 finished', tone: 'bad', icon: Layers },
      bottleneck: { label: 'Biggest Bottleneck', detail: 'Posting frequency -28%', tone: 'bad', icon: AlertTriangle }
    },
    intelligence: [
      { tag: 'PLATEAU',    accent: 'amber', text: 'Growth has flattened — variation will break it.' },
      { tag: 'WARNING',    accent: 'red',   text: 'Posting frequency is your #1 gap.' },
      { tag: 'OPPORTUNITY',accent: 'cyan',  text: 'Try a new hook format this week.' },
      { tag: 'STREAK',     accent: 'orange',text: 'Streak softening — protect it tonight.' }
    ],
    timeline: [
      { id: 'tl1', icon: Video,  accent: 'pink',   title: 'Published 2 videos',                 day: 'Mon', tag: 'Published' },
      { id: 'tl2', icon: Eye,    accent: 'cyan',   title: 'Reached 8K views',                   day: 'Wed', tag: 'Reach' },
      { id: 'tl3', icon: Users2, accent: 'green',  title: '+120 followers',                     day: 'Fri', tag: 'Audience' },
      { id: 'tl4', icon: Flame,  accent: 'orange', title: 'Streak softened to 4 days',          day: 'Sun', tag: 'Streak' }
    ],
    focus: [
      { text: 'Post 4 Shorts',           reason: 'Frequency is your biggest gap.' },
      { text: 'Try 1 new format',        reason: 'Variation breaks plateaus.' },
      { text: 'Complete 2 workflows',    reason: 'Keep rewards moving forward.' },
      { text: 'Schedule 2 evening posts',reason: 'Peak window is underused.' }
    ]
  },
  recovery: {
    label: 'Recovery Mode',
    stageAccent: 'red',
    stageNote: 'Rebuild momentum this week with Streak Protection',
    snapshot: {
      stage: { value: 'Recovery', sub: 'Stage 1 of 5', accent: 'red' },
      consistencyScore: 42,
      consistencyDelta: '-18',
      consistencyDir: 'down',
      momentum: { value: 'Declining', delta: '-32%', dir: 'down', accent: 'red' },
      audience: { value: '-180', delta: '-12%', dir: 'down', accent: 'red' },
      revenue: { value: '$24 / $200', pct: 12, dir: 'down', delta: '-44%', accent: 'red' },
      rewards: { value: 'Tier 1 — 12%', pct: 12, dir: 'down', delta: '-12%', accent: 'red' },
      bottleneck: { value: '4 missed uploads', sub: 'Streak protection recommended', accent: 'red' },
      status: { value: 'Needs Attention', tone: 'red' }
    },
    priorities: [
      { tag: 'URGENT',      accent: 'red',   priority: 1, action: 'Run Streak Protection tonight', reason: 'Recovers missed-upload momentum.', cta: 'Activate Streak Protection', link: '/app/content-studio' },
      { tag: 'HIGH IMPACT', accent: 'pink',  priority: 2, action: 'Finish 3 stalled workflows',     reason: 'Biggest unlock for next week.',      cta: 'Resume Workflow', link: '/app/content-studio' },
      { tag: 'RECOVERY',    accent: 'amber', priority: 3, action: 'Post 5 Shorts this week',        reason: 'Shorts recover engagement fastest.', cta: 'Open Content Studio', link: '/app/content-studio' },
      { tag: 'AUDIENCE',    accent: 'cyan',  priority: 4, action: 'DM 5 audience members',          reason: 'DM engagement re-warms the algorithm.', cta: 'Open Audience Inbox', link: '/app/connect/audience-inbox' }
    ],
    tasks: [
      { id: 'streak',   label: 'Run Streak Protection', sub: 'Recovers missed uploads',           impact: ['consistency', 'momentum'], accent: 'red',   icon: ShieldCheck },
      { id: 'workflow', label: 'Finish stalled workflows', sub: 'Unblocks rewards path',          impact: ['rewards'],                 accent: 'amber', icon: Layers },
      { id: 'shorts',   label: 'Upload 5 Shorts',       sub: 'Rebuild posting rhythm',            impact: ['consistency', 'momentum'], accent: 'pink',  icon: Video },
      { id: 'respond',  label: 'DM 5 audience members', sub: 'Re-warm algorithmic signals',       impact: ['momentum'],                accent: 'rose',  icon: Heart }
    ],
    rewards: {
      badge: { name: '—', earned: false },
      tier: { current: 'Tier 1', next: 'Tier 2', pct: 12 },
      bonus: 'Not eligible',
      bonusEligible: false,
      referrals: { count: 0, target: 5, pct: 0 },
      plaque: { name: 'Rising Creator', pct: 8 }
    },
    feedback: {
      best: { label: 'Best Performing Content', detail: '"Quick Tip" — 4.2K views', tone: 'mixed', icon: Star },
      weakest: { label: 'Weakest Platform', detail: 'YouTube Shorts -44%', tone: 'bad', icon: TrendingDown },
      audience: { label: 'Audience Movement', detail: '-180 net followers', tone: 'bad', icon: Users2 },
      revenue: { label: 'Revenue Change', detail: '$24 (-44%)', tone: 'bad', icon: Coins },
      referrals: { label: 'Referral Growth', detail: 'No new conversions', tone: 'bad', icon: Sparkles },
      workflow: { label: 'Workflow Performance', detail: '1 of 5 finished — 3 stalled', tone: 'bad', icon: Layers },
      bottleneck: { label: 'Biggest Bottleneck', detail: '4 missed scheduled uploads', tone: 'bad', icon: AlertTriangle }
    },
    intelligence: [
      { tag: 'RECOVERY',  accent: 'red',   text: 'Run Streak Protection tonight to recover momentum.' },
      { tag: 'HIGH IMPACT',accent: 'pink', text: '3 stalled workflows are your largest unlock.' },
      { tag: 'AUDIENCE',  accent: 'amber', text: 'Audience -180 — re-warm with DMs.' },
      { tag: 'TIMING',    accent: 'cyan',  text: 'Schedule 2 evening posts to rebuild signal.' }
    ],
    timeline: [
      { id: 'tl1', icon: Video,         accent: 'pink',   title: 'Published 1 video',          day: 'Mon', tag: 'Published' },
      { id: 'tl2', icon: AlertTriangle, accent: 'red',    title: 'Missed scheduled upload',     day: 'Wed', tag: 'Missed' },
      { id: 'tl3', icon: Flame,         accent: 'orange', title: 'Streak dropped to 2 days',    day: 'Sat', tag: 'Streak' }
    ],
    focus: [
      { text: 'Run Streak Protection',  reason: 'Recovers 4 missed uploads.' },
      { text: 'Finish 3 stalled workflows', reason: 'Largest unlock for next week.' },
      { text: 'Post 5 Shorts',          reason: 'Shorts recover engagement fastest.' },
      { text: 'Schedule 2 evening posts',reason: 'Rebuilds audience signal.' },
      { text: 'DM 5 audience members',  reason: 'Re-warms algorithmic engagement.' }
    ]
  },
  empty: {
    label: 'Brand New Creator',
    stageAccent: 'cyan',
    stageNote: 'Your growth engine starts firing after your first post',
    snapshot: {
      stage: { value: 'Getting Started', sub: 'Stage 0 of 5', accent: 'cyan' },
      consistencyScore: 0,
      consistencyDelta: '—',
      consistencyDir: 'flat',
      momentum: { value: '—', delta: '—', dir: 'flat', accent: 'white' },
      audience: { value: '—', delta: '—', dir: 'flat', accent: 'white' },
      revenue: { value: '$0 / $200', pct: 0, dir: 'flat', delta: '—', accent: 'white' },
      rewards: { value: '—', pct: 0, dir: 'flat', delta: '—', accent: 'white' },
      bottleneck: { value: 'No data yet', sub: 'Publish your first piece of content', accent: 'cyan' },
      status: { value: 'New', tone: 'cyan' }
    },
    priorities: [],
    tasks: [
      { id: 'connect',   label: 'Connect your first platform', sub: 'Required to begin tracking', impact: ['consistency'], accent: 'cyan',  icon: Radio },
      { id: 'firstpost', label: 'Schedule your first post',    sub: 'Unlocks the Consistency engine', impact: ['consistency','momentum'], accent: 'pink', icon: Video },
      { id: 'goal',      label: 'Set a weekly posting goal',   sub: 'Goals power your digest + rewards', impact: ['rewards'], accent: 'amber', icon: Target }
    ],
    rewards: {
      badge: { name: '—', earned: false },
      tier: { current: '—', next: 'Tier 1', pct: 0 },
      bonus: 'Not eligible',
      bonusEligible: false,
      referrals: { count: 0, target: 5, pct: 0 },
      plaque: { name: '—', pct: 0 }
    },
    feedback: null,
    intelligence: [],
    timeline: [],
    focus: [
      { text: 'Connect your first platform', reason: 'We need a signal source to begin tracking.' },
      { text: 'Schedule your first post',    reason: 'Your first publish unlocks the engine.' },
      { text: 'Set a weekly posting goal',   reason: 'Goals power your weekly digest.' },
      { text: 'Explore the Rewards system',  reason: 'See what milestones you unlock as you grow.' }
    ]
  }
};

const STATE_OPTIONS = [
  { value: 'momentum', label: 'Momentum Building' },
  { value: 'strong',   label: 'Strong Growth' },
  { value: 'plateau',  label: 'Plateau' },
  { value: 'recovery', label: 'Recovery Mode' },
  { value: 'empty',    label: 'Brand New (Empty)' }
];

// ============================================================
// Helpers
// ============================================================
const Dir = ({ dir, className = '' }) => {
  if (dir === 'up')   return <ChevronUp   className={`h-3 w-3 ${className}`} />;
  if (dir === 'down') return <ChevronDown className={`h-3 w-3 ${className}`} />;
  return <Minus className={`h-3 w-3 ${className}`} />;
};

const TrendChip = ({ delta, dir }) => {
  const cls =
    dir === 'up'   ? 'border-green-500/30 text-green-300 bg-green-500/10' :
    dir === 'down' ? 'border-red-500/30 text-red-300 bg-red-500/10' :
                     'border-white/10 text-[#8A93A6] bg-white/[0.03]';
  return (
    <Badge variant="outline" className={`text-[10px] font-medium tracking-wide gap-1 px-1.5 py-0 h-4 ${cls}`}>
      <Dir dir={dir} />
      {delta}
    </Badge>
  );
};

const SourceChip = ({ source }) => (
  <span className="inline-flex items-center gap-1 text-[9px] font-medium uppercase tracking-[0.14em] text-[#555E6E]">
    <span className="w-1 h-1 rounded-full bg-[#3A4150]" />
    {source}
  </span>
);

const SectionHeader = ({ icon: Icon, accent = 'cyan', title, subtitle, source, right }) => (
  <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
    <div>
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 text-${accent}-400`} />
        <h2 className="text-base font-semibold text-white">{title}</h2>
      </div>
      {subtitle && <p className="text-[11px] text-[#8A93A6] mt-0.5">{subtitle}</p>}
    </div>
    <div className="flex items-center gap-3">
      {source && <SourceChip source={source} />}
      {right}
    </div>
  </div>
);

// ----- Section 1 cards -----
const StageCard = ({ s }) => {
  const accent = s.stage.accent;
  return (
    <Card className="glass border-white/5 col-span-2 md:col-span-2 lg:col-span-2 relative overflow-hidden" data-testid="growth-stage-card">
      <div className={`absolute inset-0 bg-gradient-to-br from-${accent}-500/10 via-transparent to-transparent pointer-events-none`} />
      <CardContent className="p-4 relative">
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-[10px] uppercase tracking-[0.14em] text-[#8A93A6] font-medium">Growth Stage</p>
          <Badge variant="outline" className={`text-[10px] uppercase tracking-wider border-${accent}-500/30 text-${accent}-300 bg-${accent}-500/10`}>
            {s.status.value}
          </Badge>
        </div>
        <p className={`text-2xl font-bold text-${accent}-200 leading-none mb-1`}>{s.stage.value}</p>
        <p className="text-[11px] text-[#8A93A6]">{s.stage.sub}</p>
      </CardContent>
    </Card>
  );
};

const ConsistencyCard = ({ score, delta, dir }) => {
  const accent = score >= 85 ? 'green' : score >= 65 ? 'amber' : score > 0 ? 'red' : 'white';
  return (
    <Card className="glass border-white/5" data-testid="growth-consistency-card">
      <CardContent className="p-3 relative">
        <div className="flex items-center justify-between mb-2">
          <div className="w-6 h-6 rounded-md bg-orange-500/10 border border-orange-500/20 flex items-center justify-center">
            <Flame className="h-3 w-3 text-orange-400" />
          </div>
          <div className="flex items-center gap-1">
            <TrendChip delta={delta} dir={dir} />
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" className="text-[#555E6E] hover:text-white transition-colors" aria-label="Consistency Score info">
                    <HelpCircle className="h-3 w-3" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="left" className="bg-[#1C1F26] border border-white/10 text-white max-w-[240px] p-3">
                  <p className="text-[11px] font-semibold mb-1.5">Consistency Score</p>
                  <p className="text-[10px] text-[#8A93A6] mb-2 leading-relaxed">A composite signal from your ecosystem activity:</p>
                  <ul className="text-[10px] text-[#C9CFDA] space-y-0.5 leading-relaxed">
                    <li>• Posting consistency</li>
                    <li>• Workflow completion</li>
                    <li>• Audience engagement</li>
                    <li>• Platform activity</li>
                    <li>• Goal completion</li>
                  </ul>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
        <p className="text-[9px] uppercase tracking-[0.14em] text-[#8A93A6] mb-0.5 font-medium">Consistency Score</p>
        <div className="flex items-baseline gap-1.5">
          <p className={`text-2xl font-bold tabular-nums leading-none text-${accent}-300`}>{score || '—'}</p>
          {score > 0 && <p className="text-[10px] text-[#8A93A6]">/100</p>}
        </div>
        <p className="text-[9px] text-[#555E6E] mt-1.5">ecosystem composite</p>
      </CardContent>
    </Card>
  );
};

const MetricCard = ({ label, value, delta, dir, accent = 'cyan', icon: Icon, sub, progressPct }) => (
  <Card className="glass border-white/5 hover:border-white/10 transition-colors">
    <CardContent className="p-3">
      <div className="flex items-center justify-between mb-2">
        <div className={`w-6 h-6 rounded-md bg-${accent}-500/10 border border-${accent}-500/20 flex items-center justify-center`}>
          <Icon className={`h-3 w-3 text-${accent}-400`} />
        </div>
        {delta && <TrendChip delta={delta} dir={dir} />}
      </div>
      <p className="text-[9px] uppercase tracking-[0.14em] text-[#8A93A6] mb-0.5 font-medium">{label}</p>
      <p className="text-xl font-bold text-white tabular-nums leading-tight">{value}</p>
      {sub && <p className="text-[10px] text-[#8A93A6] mt-0.5 truncate">{sub}</p>}
      {typeof progressPct === 'number' && (
        <Progress
          value={progressPct}
          className={`h-1 mt-2 bg-white/[0.04] ${
            progressPct >= 100 ? '[&>div]:bg-green-400' :
            progressPct >= 60  ? '[&>div]:bg-cyan-400'  :
            progressPct > 0    ? '[&>div]:bg-amber-400' : '[&>div]:bg-white/20'
          }`}
        />
      )}
    </CardContent>
  </Card>
);

// ----- Section 2 priorities -----
const PRIORITY_TONE = {
  1: 'ring-2 ring-pink-500/25 border-pink-500/30 bg-gradient-to-br from-pink-500/[0.05] to-transparent',
  2: 'border-white/10 bg-white/[0.025]',
  3: 'border-white/8 bg-white/[0.02]',
  4: 'border-white/8 bg-white/[0.02]'
};

const PriorityCard = ({ p }) => {
  const isTop = p.priority === 1;
  return (
    <Card className={`glass transition-all hover:translate-y-[-1px] hover:border-white/15 ${PRIORITY_TONE[p.priority] || 'border-white/5'}`} data-testid={`priority-${p.priority}`}>
      <CardContent className="p-3.5">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={`text-[10px] font-bold uppercase tracking-[0.12em] border-${p.accent}-500/40 text-${p.accent}-200 bg-${p.accent}-500/15 px-2 py-0.5`}>
              {p.tag}
            </Badge>
            {isTop && (
              <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider text-pink-300 font-semibold">
                <Sparkles className="h-2.5 w-2.5" />
                Top Move
              </span>
            )}
          </div>
          <span className="text-[9px] uppercase tracking-wider text-[#555E6E] tabular-nums">#{p.priority}</span>
        </div>
        <p className="text-[13px] text-white leading-relaxed font-medium mb-1">{p.action}</p>
        <p className="text-[11px] text-[#8A93A6] leading-relaxed mb-3">
          <Info className="h-3 w-3 inline-block mr-1 -mt-0.5 opacity-70" />
          {p.reason}
        </p>
        <Link to={p.link} className={`text-[11px] font-semibold uppercase tracking-wider inline-flex items-center gap-1.5 transition-colors ${isTop ? 'text-pink-300 hover:text-pink-200' : 'text-[#8A93A6] hover:text-white'}`}>
          {p.cta}
          <ArrowRight className="h-3 w-3" />
        </Link>
      </CardContent>
    </Card>
  );
};

// ----- Section 3 task row -----
const IMPACT_LABEL = {
  consistency: { label: 'Consistency', accent: 'orange' },
  momentum:    { label: 'Momentum',    accent: 'cyan' },
  rewards:     { label: 'Rewards',     accent: 'amber' }
};

const TaskRow = ({ task, done, onToggle }) => {
  const Icon = task.icon;
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`w-full flex items-start gap-3 rounded-lg border px-3.5 py-3 text-left transition-colors ${
        done
          ? 'border-green-500/25 bg-green-500/[0.05]'
          : 'border-white/5 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/10'
      }`}
      data-testid={`task-${task.id}`}
    >
      {done ? (
        <CheckCircle2 className="h-4 w-4 text-green-400 shrink-0 mt-0.5" />
      ) : (
        <Circle className="h-4 w-4 text-[#555E6E] shrink-0 mt-0.5" />
      )}
      <div className={`w-7 h-7 rounded-md bg-${task.accent}-500/10 border border-${task.accent}-500/20 flex items-center justify-center shrink-0`}>
        <Icon className={`h-3.5 w-3.5 text-${task.accent}-400`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-[13px] font-medium leading-tight ${done ? 'text-[#8A93A6] line-through' : 'text-white'}`}>{task.label}</p>
        <p className={`text-[11px] mt-0.5 ${done ? 'text-[#555E6E]' : 'text-[#8A93A6]'}`}>{task.sub}</p>
      </div>
      <div className="hidden md:flex items-center gap-1 shrink-0">
        {task.impact.map((k) => {
          const meta = IMPACT_LABEL[k];
          if (!meta) return null;
          return (
            <Badge key={k} variant="outline" className={`text-[9px] uppercase tracking-wider border-${meta.accent}-500/30 text-${meta.accent}-300 bg-${meta.accent}-500/10 px-1.5 py-0`}>
              {meta.label}
            </Badge>
          );
        })}
      </div>
      {done && (
        <span className="text-[10px] uppercase tracking-wider text-green-300 font-semibold mt-0.5 ml-2">Done</span>
      )}
    </button>
  );
};

// ----- Feedback row -----
const TONE_MAP = {
  good:  { border: 'border-green-500/20', bg: 'bg-green-500/[0.04]',  text: 'text-green-300',  dotAccent: 'green' },
  bad:   { border: 'border-red-500/20',   bg: 'bg-red-500/[0.04]',    text: 'text-red-300',    dotAccent: 'red' },
  mixed: { border: 'border-amber-500/20', bg: 'bg-amber-500/[0.04]',  text: 'text-amber-300',  dotAccent: 'amber' }
};

const FeedbackRow = ({ icon: Icon, label, detail, tone }) => {
  const t = TONE_MAP[tone] || TONE_MAP.mixed;
  return (
    <div className={`flex items-start gap-2.5 rounded-md border ${t.border} ${t.bg} px-3 py-2`}>
      <div className={`w-7 h-7 rounded-md bg-${t.dotAccent}-500/10 border border-${t.dotAccent}-500/20 flex items-center justify-center shrink-0`}>
        <Icon className={`h-3.5 w-3.5 text-${t.dotAccent}-400`} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-white truncate">{label}</p>
        <p className={`text-[11px] ${t.text} truncate`}>{detail}</p>
      </div>
    </div>
  );
};

// ----- Empty state -----
const EmptyState = ({ icon: Icon, title, detail, ctaLabel, ctaLink, accent = 'cyan' }) => (
  <div className="rounded-md border border-dashed border-white/10 bg-white/[0.015] px-4 py-6 flex flex-col items-center text-center">
    <div className={`w-9 h-9 rounded-full bg-${accent}-500/10 border border-${accent}-500/20 flex items-center justify-center mb-2`}>
      <Icon className={`h-4 w-4 text-${accent}-400`} />
    </div>
    <p className="text-xs font-semibold text-white">{title}</p>
    <p className="text-[11px] text-[#8A93A6] mt-1 max-w-[280px] leading-relaxed">{detail}</p>
    {ctaLabel && ctaLink && (
      <Link to={ctaLink} className="mt-3">
        <Button variant="outline" size="sm" className="h-7 text-[11px]">
          {ctaLabel}
          <ArrowRight className="h-3 w-3 ml-1.5" />
        </Button>
      </Link>
    )}
  </div>
);

// ============================================================
// MAIN
// ============================================================
export default function CreatorGrowthEngine({ mode = 'full' } = {}) {
  const { user } = useContext(AuthContext);
  const isAdmin = !!user?.is_internal_admin;
  const [stateKey, setStateKey] = useState('momentum');
  const [taskState, setTaskState] = useState({});

  // Reset task completions on state switch
  useEffect(() => { setTaskState({}); }, [stateKey]);

  const data = STATES[stateKey];

  const toggleTask = (id) => setTaskState((s) => ({ ...s, [id]: !s[id] }));
  const completedCount = useMemo(
    () => data.tasks.filter((t) => taskState[t.id]).length,
    [data.tasks, taskState]
  );
  const totalTasks = data.tasks.length;
  const taskPct = totalTasks ? Math.round((completedCount / totalTasks) * 100) : 0;

  const isEmpty = stateKey === 'empty';

  // Each section below is a plain JSX value computed once so the SAME
  // existing section content can be reused across render modes (full /
  // intelligence / rewards, see bottom of this component) without any
  // section's internal content or behavior being modified.

  // SECTION 1 — Growth Snapshot (Flywheel health)
  const sectionSnapshot = (
      <section className="mb-7" data-testid="section-snapshot">
        <SectionHeader
          icon={Activity}
          accent="cyan"
          title="Growth Snapshot"
          subtitle={data.stageNote}
          source="Flywheel"
        />
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2.5">
          <StageCard s={data.snapshot} />
          <ConsistencyCard
            score={data.snapshot.consistencyScore}
            delta={data.snapshot.consistencyDelta}
            dir={data.snapshot.consistencyDir}
          />
          <MetricCard label="Weekly Momentum"  value={data.snapshot.momentum.value}  delta={data.snapshot.momentum.delta}  dir={data.snapshot.momentum.dir}  accent={data.snapshot.momentum.accent}  icon={Activity} />
          <MetricCard label="Audience Growth"  value={data.snapshot.audience.value}  delta={data.snapshot.audience.delta}  dir={data.snapshot.audience.dir}  accent={data.snapshot.audience.accent}  icon={Users2} />
          <MetricCard label="Revenue Progress" value={data.snapshot.revenue.value}   delta={data.snapshot.revenue.delta}   dir={data.snapshot.revenue.dir}   accent={data.snapshot.revenue.accent}   icon={Coins}    progressPct={data.snapshot.revenue.pct} />
          <MetricCard label="Reward Progress"  value={data.snapshot.rewards.value}   delta={data.snapshot.rewards.delta}   dir={data.snapshot.rewards.dir}   accent={data.snapshot.rewards.accent}   icon={Award}    progressPct={data.snapshot.rewards.pct} />
        </div>
        {/* Bottleneck callout — full width below */}
        <Card className={`glass mt-2.5 border-${data.snapshot.bottleneck.accent}-500/25 bg-${data.snapshot.bottleneck.accent}-500/[0.04]`} data-testid="bottleneck-callout">
          <CardContent className="p-3 flex items-start gap-3">
            <div className={`w-8 h-8 rounded-md bg-${data.snapshot.bottleneck.accent}-500/15 border border-${data.snapshot.bottleneck.accent}-500/25 flex items-center justify-center shrink-0`}>
              <AlertTriangle className={`h-4 w-4 text-${data.snapshot.bottleneck.accent}-400`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.14em] text-[#8A93A6] font-medium">Biggest Bottleneck</p>
              <div className="flex items-baseline gap-2 flex-wrap">
                <p className={`text-sm font-semibold text-${data.snapshot.bottleneck.accent}-200`}>{data.snapshot.bottleneck.value}</p>
                <p className="text-[11px] text-[#8A93A6]">{data.snapshot.bottleneck.sub}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>
  );

  // SECTION 2 — Growth Priorities (Decision Engine)
  const sectionPriorities = (
      <section className="mb-7" data-testid="section-priorities">
        <SectionHeader
          icon={Zap}
          accent="pink"
          title="Growth Priorities"
          subtitle="AI-ranked recommendations from the Decision Engine."
          source="Decision Engine"
        />
        {data.priorities.length === 0 ? (
          <EmptyState
            icon={Lightbulb}
            accent="pink"
            title="Priorities unlock after your first week"
            detail="Once you publish, the Decision Engine ranks high-impact moves here based on your activity."
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.priorities.map((p) => <PriorityCard key={p.priority} p={p} />)}
          </div>
        )}
      </section>
  );

  // SECTION 3 — Creator Task Engine (Execution)
  const sectionTasks = (
      <section className="mb-7" data-testid="section-tasks">
        <SectionHeader
          icon={Target}
          accent="green"
          title="Creator Task Engine"
          subtitle="Each task you complete moves Consistency, Momentum, and Rewards forward."
          source="Execution"
          right={
            <div className="flex items-center gap-2">
              <div className="hidden sm:flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wider text-[#8A93A6]">Progress</span>
                <Progress value={taskPct} className="h-1.5 w-28 bg-white/[0.04] [&>div]:bg-green-400" />
                <span className="text-[11px] text-white tabular-nums font-semibold">{completedCount}/{totalTasks}</span>
              </div>
            </div>
          }
        />
        <Card className="glass border-white/5">
          <CardContent className="p-4 space-y-2">
            {data.tasks.map((t) => (
              <TaskRow key={t.id} task={t} done={!!taskState[t.id]} onToggle={() => toggleTask(t.id)} />
            ))}
            <p className="text-[10px] text-[#8A93A6] pt-1">
              Completing tasks updates your Consistency Score, Momentum, and Rewards Progress in real time.
            </p>
          </CardContent>
        </Card>
      </section>
  );

  // SECTION 4 — Rewards Progress
  const sectionRewards = (
      <section className="mb-7" data-testid="section-rewards">
        <SectionHeader
          icon={Award}
          accent="amber"
          title="Rewards Progress"
          subtitle="Effort → reward. Every action moves you closer to your next unlock."
          source="Rewards"
          right={
            <Link to="/app/account/rewards">
              <Button variant="outline" size="sm" data-testid="view-rewards-btn">
                View Rewards
                <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
              </Button>
            </Link>
          }
        />
        <Card className="glass border-white/5">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className={`rounded-md border ${data.rewards.badge.earned ? 'border-amber-500/25 bg-amber-500/[0.05]' : 'border-white/5 bg-white/[0.02]'} px-3 py-2.5`}>
                <p className="text-[10px] uppercase tracking-wider text-[#8A93A6]">Badge</p>
                <p className={`text-sm font-semibold ${data.rewards.badge.earned ? 'text-amber-200' : 'text-white'}`}>{data.rewards.badge.name}</p>
                <p className="text-[10px] text-[#8A93A6] mt-0.5">{data.rewards.badge.earned ? 'Earned' : 'Pending'}</p>
              </div>
              <div className="rounded-md border border-white/5 bg-white/[0.02] px-3 py-2.5">
                <p className="text-[10px] uppercase tracking-wider text-[#8A93A6] mb-1">Tier Progress</p>
                <p className="text-[11px] text-white">{data.rewards.tier.current} → {data.rewards.tier.next}</p>
                <Progress value={data.rewards.tier.pct} className="h-1 bg-white/[0.04] [&>div]:bg-cyan-400 mt-1" />
                <p className="text-[10px] text-white tabular-nums mt-1">{data.rewards.tier.pct}%</p>
              </div>
              <div className={`rounded-md border ${data.rewards.bonusEligible ? 'border-green-500/25 bg-green-500/[0.05]' : 'border-white/5 bg-white/[0.02]'} px-3 py-2.5`}>
                <p className="text-[10px] uppercase tracking-wider text-[#8A93A6]">Bonus</p>
                <p className={`text-sm font-semibold ${data.rewards.bonusEligible ? 'text-green-300' : 'text-white'}`}>{data.rewards.bonus}</p>
              </div>
              <div className="rounded-md border border-white/5 bg-white/[0.02] px-3 py-2.5">
                <p className="text-[10px] uppercase tracking-wider text-[#8A93A6] mb-1">Referrals</p>
                <p className="text-[11px] text-white tabular-nums">{data.rewards.referrals.count} / {data.rewards.referrals.target}</p>
                <Progress value={data.rewards.referrals.pct} className="h-1 bg-white/[0.04] [&>div]:bg-pink-400 mt-1" />
              </div>
              <div className="rounded-md border border-white/5 bg-white/[0.02] px-3 py-2.5">
                <p className="text-[10px] uppercase tracking-wider text-[#8A93A6] mb-1">Plaque</p>
                <p className="text-[11px] text-white">{data.rewards.plaque.name}</p>
                <Progress value={data.rewards.plaque.pct} className="h-1 bg-white/[0.04] [&>div]:bg-amber-400 mt-1" />
                <p className="text-[10px] text-white tabular-nums mt-1">{data.rewards.plaque.pct}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>
  );

  // SECTION 5 — Growth Feedback
  const sectionFeedback = (
      <section className="mb-7" data-testid="section-feedback">
        <SectionHeader
          icon={RefreshCw}
          accent="cyan"
          title="Growth Feedback"
          subtitle="What's helping and hurting your growth this week."
          source="Flywheel"
        />
        {!data.feedback ? (
          <EmptyState
            icon={Compass}
            accent="cyan"
            title="Feedback loop activates after your first publish"
            detail="Once content is live, you'll see exactly what's driving — and blocking — your growth."
          />
        ) : (
          <Card className="glass border-white/5">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                <FeedbackRow {...data.feedback.best} />
                <FeedbackRow {...data.feedback.weakest} />
                <FeedbackRow {...data.feedback.audience} />
                <FeedbackRow {...data.feedback.revenue} />
                <FeedbackRow {...data.feedback.referrals} />
                <FeedbackRow {...data.feedback.workflow} />
              </div>
              <div className="mt-3">
                <FeedbackRow {...data.feedback.bottleneck} />
              </div>
            </CardContent>
          </Card>
        )}
      </section>
  );

  // SECTION 6 — Weekly Intelligence
  const sectionIntelligence = (
      <section className="mb-7" data-testid="section-intelligence">
        <SectionHeader
          icon={Lightbulb}
          accent="violet"
          title="Weekly Intelligence"
          subtitle="Personalized creator coaching, distilled from your weekly data."
          source="Coaching"
        />
        {data.intelligence.length === 0 ? (
          <EmptyState
            icon={Lightbulb}
            accent="violet"
            title="Coaching unlocks after your first week"
            detail="Once you publish a few pieces of content, the engine surfaces insights specific to your patterns."
          />
        ) : (
          <Card className="glass border-white/5">
            <CardContent className="p-4 space-y-2">
              {data.intelligence.map((i, idx) => (
                <div key={idx} className="flex items-start gap-3 rounded-md border border-white/5 bg-white/[0.02] px-3 py-2.5" data-testid={`intelligence-${idx}`}>
                  <Badge variant="outline" className={`text-[10px] font-bold uppercase tracking-[0.12em] border-${i.accent}-500/40 text-${i.accent}-200 bg-${i.accent}-500/15 px-2 py-0.5 shrink-0`}>
                    {i.tag}
                  </Badge>
                  <p className="text-[13px] text-white leading-relaxed flex-1">{i.text}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </section>
  );

  // SECTION 7 — Growth Timeline
  const sectionTimeline = (
      <section className="mb-7" data-testid="section-timeline">
        <SectionHeader
          icon={Clock}
          accent="cyan"
          title="Growth Timeline"
          subtitle="Recent wins and milestones — the motivational loop."
          source="Activity"
        />
        <Card className="glass border-white/5">
          <CardContent className="p-5">
            {data.timeline.length === 0 ? (
              <EmptyState
                icon={PlayCircle}
                accent="cyan"
                title="Your timeline starts here"
                detail="Each badge earned, milestone hit, and referral converted appears here. Publish your first piece of content to begin."
                ctaLabel="Open Content Studio"
                ctaLink="/app/content-studio"
              />
            ) : (
              <div className="relative">
                <span aria-hidden className="absolute left-[3.6rem] top-3 bottom-3 w-px bg-gradient-to-b from-cyan-500/0 via-white/10 to-cyan-500/0" />
                <div className="space-y-3">
                  {data.timeline.map((t) => {
                    const Icon = t.icon;
                    return (
                      <div key={t.id} className="relative flex items-center gap-4" data-testid={`timeline-${t.id}`}>
                        <span className="text-[10px] uppercase tracking-[0.14em] text-[#8A93A6] w-10 tabular-nums font-semibold">{t.day}</span>
                        <div className={`relative z-10 w-9 h-9 rounded-full bg-[#0F1218] border border-${t.accent}-500/40 flex items-center justify-center shrink-0 shadow-[0_0_0_3px_rgba(255,255,255,0.02)]`}>
                          <Icon className={`h-4 w-4 text-${t.accent}-400`} />
                        </div>
                        <div className="flex-1 min-w-0 flex items-center justify-between gap-3">
                          <p className="text-[13px] text-white truncate font-medium">{t.title}</p>
                          <Badge variant="outline" className={`text-[10px] uppercase tracking-wider border-${t.accent}-500/30 text-${t.accent}-300 bg-${t.accent}-500/10 shrink-0`}>
                            {t.tag}
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </section>
  );

  // SECTION 8 — Recommended Focus
  const sectionFocus = (
      <section data-testid="section-focus">
        <SectionHeader
          icon={Rocket}
          accent="pink"
          title="Recommended Focus"
          subtitle="Your next-week execution plan, ranked by impact."
          source="Decision Engine + Task Engine"
        />
        <Card className="glass border-pink-500/20 ring-1 ring-pink-500/10 relative overflow-hidden">
          <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-pink-500/10 blur-3xl pointer-events-none" />
          <CardContent className="p-4 relative">
            <div className="space-y-2">
              {data.focus.map((f, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-3 rounded-lg border border-white/5 bg-white/[0.02] px-3.5 py-2.5"
                  data-testid={`focus-item-${idx}`}
                >
                  <Circle className="h-4 w-4 text-[#555E6E] shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium text-white leading-tight">{f.text}</p>
                    <p className="text-[11px] text-[#8A93A6] mt-1 leading-relaxed">
                      <Info className="h-3 w-3 inline-block mr-1 -mt-0.5 opacity-70" />
                      {f.reason}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-[#8A93A6] mt-3">
              This list is regenerated each week based on your growth signals.
            </p>
          </CardContent>
        </Card>
      </section>
  );

  return (
    <div className={mode === 'full' ? 'container mx-auto p-6 max-w-6xl' : ''} data-testid="creator-growth-engine-page">
      {mode === 'full' && (
      <>
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <Gauge className="h-6 w-6 text-cyan-400" />
            <h1 className="text-3xl font-bold text-white">Creator Growth Engine</h1>
            <Badge variant="outline" className="text-[10px] border-cyan-500/30 text-cyan-300 bg-cyan-500/10 uppercase tracking-wider font-semibold">
              Growth OS
            </Badge>
            <Badge variant="outline" className="text-[10px] border-violet-500/30 text-violet-300 bg-violet-500/10 uppercase tracking-wider font-semibold">
              Personalized
            </Badge>
            {isAdmin && (
              <Badge variant="outline" className="text-[10px] border-pink-500/30 text-pink-300 bg-pink-500/10 uppercase tracking-wider font-semibold">
                Admin Preview
              </Badge>
            )}
          </div>
          <p className="text-[#8A93A6] max-w-2xl">
            Your creator business operating system. Where you are, what to focus on, and what to do next — generated from your entire Consistency ecosystem.
          </p>
        </div>

        <div className="flex items-center gap-2 self-start">
          {isAdmin && (
            <Select value={stateKey} onValueChange={setStateKey}>
              <SelectTrigger className="h-9 w-[200px] bg-pink-500/[0.06] border-pink-500/25 text-pink-200 text-xs" data-testid="growth-state-select">
                <Sparkles className="h-3.5 w-3.5 mr-1 text-pink-400" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATE_OPTIONS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Link to="/app/account/weekly-digest">
            <Button variant="outline" size="sm" data-testid="open-weekly-digest-btn">
              Weekly Digest
              <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
            </Button>
          </Link>
        </div>
      </div>
      </>
      )}

      {mode !== 'full' && isAdmin && (
        <div className="mb-4 flex justify-end">
          <Select value={stateKey} onValueChange={setStateKey}>
            <SelectTrigger className="h-9 w-[200px] bg-pink-500/[0.06] border-pink-500/25 text-pink-200 text-xs" data-testid="growth-state-select">
              <Sparkles className="h-3.5 w-3.5 mr-1 text-pink-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATE_OPTIONS.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* mode="intelligence" (Project Manager → Growth Intelligence tile): every
         section except Rewards Progress, which now lives exclusively in the
         Milestones & Rewards tile to avoid duplicating official reward tracking.
         mode="rewards" (Project Manager → Milestones & Rewards tile): just the
         existing Rewards Progress section, reused as-is.
         mode="full" (default/standalone): unchanged original behavior. */}
      {mode === 'intelligence' ? (
        <>
          {sectionSnapshot}
          {sectionPriorities}
          {sectionTasks}
          {sectionFeedback}
          {sectionIntelligence}
          {sectionFocus}
          {sectionTimeline}
        </>
      ) : mode === 'rewards' ? (
        <>{sectionRewards}</>
      ) : (
        <>
          {sectionSnapshot}
          {sectionPriorities}
          {sectionTasks}
          {sectionRewards}
          {sectionFeedback}
          {sectionIntelligence}
          {sectionTimeline}
          {sectionFocus}
        </>
      )}
    </div>
  );
}
